import { useEffect, useRef, useState } from 'react';
import { resolvePose, type PublicMascot } from '@shared';
import { apiUrl } from '../../lib/api';
import { imageUrl } from '../../lib/format';
import { useSession } from '../../state/session';
import { MascotPlaceholder } from './MascotPlaceholder';
import { MASCOT_STATES, PLACEHOLDER_FACES, type MascotState } from './states';
import styles from './Mascot.module.css';

/** Uploaded art is served by our own server; anything else is an external URL. */
function sourceUrl(src: string): string {
  return src.startsWith('/mascot/') ? apiUrl(src) : imageUrl(src);
}

interface MascotProps {
  /** What it is doing. Data-driven — see `states.ts`. */
  state?: MascotState;
  /** Edge length in px. The art is square. */
  size?: number;
  /**
   * `stage` is the full presentation: a glow behind and a shadow underneath.
   * `avatar` is the bare figure, for tight spaces like the console's top bar.
   */
  variant?: 'stage' | 'avatar';
  /** Draw another mascot than the active realm's — the console's previews. */
  mascot?: PublicMascot | null;
  className?: string;
}

/**
 * A realm's mascot.
 *
 * Shows the art the realm dropped in for the current state's pose, and the themed
 * placeholder when it has none (or the image fails to load) — so nothing in the app
 * waits on art existing. The motion is plain CSS and moves only `transform` and
 * `opacity`; the blink, when the realm supplied a `blink` image, is a timed swap.
 */
export function Mascot({
  state = 'idle',
  size = 120,
  variant = 'stage',
  mascot: given,
  className,
}: MascotProps) {
  const session = useSession();
  const mascot = given === undefined ? session.mascot : given;
  const definition = MASCOT_STATES[state];

  const resolved = resolvePose(mascot, definition.pose);
  const blinkSrc = definition.blink ? mascot?.poses.blink : undefined;

  // An image that fails to load must not leave a hole: fall back to the drawing.
  const [failed, setFailed] = useState<string | null>(null);
  const [blinking, setBlinking] = useState(false);

  // Decode every pose once, up front, into the browser's cache. The crossfade
  // swaps a new <img> in at the exact moment the state changes — mid-spin, when
  // the frame budget is tightest — and an undecoded PNG decoded on that frame is
  // what dropped the animation to a crawl and flashed an empty figure between
  // poses. Decoding ahead of time means the swap is a cache hit: no decode, no
  // layout, just the opacity fade. Keyed on the set of URLs, so it re-runs only
  // when the realm's art actually changes, never on a plain state swap.
  const poseUrls = mascot ? Object.values(mascot.poses) : [];
  const poseKey = poseUrls.join('|');
  useEffect(() => {
    if (poseUrls.length === 0) return;
    const images = poseUrls.map((src) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = sourceUrl(src);
      // decode() rejects if the browser can't (e.g. a broken URL); the real
      // <img> handles that case with its own onError, so swallow it here.
      void img.decode?.().catch(() => {});
      return img;
    });
    return () => {
      // Drop references so the decoded bitmaps can be reclaimed if the realm's
      // art changes; the HTTP cache still serves the next mount instantly.
      for (const img of images) img.src = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poseKey]);

  // The pose image to show, independent of the blink (which is a quick overlay
  // on top of it, not a state change). `undefined` means "no art — draw the
  // placeholder". A pose whose image failed to load is treated as no art.
  const poseSrc = resolved && resolved.src !== failed ? resolved.src : undefined;

  useEffect(() => {
    if (!blinkSrc) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(
        () => {
          setBlinking(true);
          timer = setTimeout(() => {
            setBlinking(false);
            schedule();
          }, 140);
        },
        3000 + Math.random() * 3500,
      );
    };
    schedule();
    return () => clearTimeout(timer);
  }, [blinkSrc]);

  // Crossfade between poses. Each pose change pushes a new layer; the incoming
  // one fades in over the outgoing one (still fully opaque underneath), then the
  // old layer is pruned once the fade is done — so idle→wave→celebrate dissolve
  // rather than cutting. The blink never enters this stack; it is swapped on a
  // fixed overlay above, so a 140 ms wink does not trigger a crossfade.
  const layerId = useRef(0);
  const [layers, setLayers] = useState<{ src: string; id: number }[]>(() =>
    poseSrc ? [{ src: poseSrc, id: layerId.current }] : [],
  );

  useEffect(() => {
    setLayers((prev) => {
      const top = prev[prev.length - 1];
      if (top?.src === poseSrc) return prev;
      if (!poseSrc) return [];
      return [...prev, { src: poseSrc, id: (layerId.current += 1) }];
    });
  }, [poseSrc]);

  useEffect(() => {
    if (layers.length <= 1) return;
    const timer = setTimeout(() => setLayers((prev) => prev.slice(-1)), 480);
    return () => clearTimeout(timer);
  }, [layers]);

  const useImage = layers.length > 0;

  return (
    <div
      className={`${styles.root} ${variant === 'avatar' ? styles.avatar : ''} ${className ?? ''}`}
      style={{ width: size, height: size }}
      data-state={state}
      data-motion={definition.motion}
      role="img"
      aria-label="Realm mascot"
    >
      {variant === 'stage' && <span className={styles.halo} aria-hidden="true" />}
      <span className={styles.figure}>
        {useImage ? (
          <>
            {layers.map((layer) => (
              <img
                key={layer.id}
                className={`${styles.art} ${styles.poseLayer}`}
                src={sourceUrl(layer.src)}
                alt=""
                draggable={false}
                decoding="async"
                onError={() => setFailed(layer.src)}
              />
            ))}
            {blinking && blinkSrc && (
              <img
                className={`${styles.art} ${styles.blinkLayer}`}
                src={sourceUrl(blinkSrc)}
                alt=""
                draggable={false}
                decoding="async"
              />
            )}
          </>
        ) : (
          <MascotPlaceholder face={PLACEHOLDER_FACES[state]} />
        )}
      </span>
      {variant === 'stage' && <span className={styles.shadow} aria-hidden="true" />}
      {definition.sparkle && (
        <span className={styles.sparkles} aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </span>
      )}
    </div>
  );
}
